import { useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { Helmet } from "react-helmet";
import { Link as RouterLink } from "react-router-dom";
import ContainerPage from "../../components/Containers/ContainerPage";
import TitlePage from "../../components/Texts/TitlePage";
import Loader from "../../components/Loaders/Loader";
import ShareModalMultiple from "../../components/Gallery/ShareModalMultiple/ShareModalMultiple";
import CompetitorScopeFlowModals from "../../components/CompetitorScope/CompetitorScopeFlowModals";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import {
  startListenerHomeFeatureActivity,
  stopListenerHomeFeatureActivity,
} from "../../actions/homeFeatureActivity";
import {
  HOME_PAGE_TITLE,
  HOME_SECTION_CONTINUE,
  HOME_SECTION_CONTINUE_HINT,
  HOME_SECTION_DISCOVER,
  HOME_SECTION_DISCOVER_HINT,
  HOME_SUBTITLE,
  HOME_WELCOME_TITLE,
} from "../../i18n/keysTranslations";
import { HOME_FEATURES } from "../../data/homeFeatures";
import useHomeFeatures from "../../hooks/useHomeFeatures";
import useHomeFeatureActions from "../../hooks/useHomeFeatureActions";
import useOutsideCampaignGallery from "../../hooks/useOutsideCampaignGallery";
import FeatureCard from "./components/FeatureCard";
import { INPUT_COLOR_BORDER } from "../../utils/colors";
import OnboardingSetupSteps from "../../components/Onboarding/OnboardingSetupSteps";
import ModalCreateWithIA from "../../components/Modal/ModalCreateWithIA";

const getFeatureChipProps = (action) => {
  switch (action.type) {
    case "run":
      return {
        onClick: action.onClick,
        clickable: true,
      };
    case "navigate":
      return {
        component: RouterLink,
        to: action.to,
        clickable: true,
      };
    case "none":
      return {};
    default: {
      const _exhaustiveCheck = action;
      void _exhaustiveCheck;
      return {};
    }
  }
};

const Dashboard = () => {
  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();
  const theme = useSelectorApp((state) => state.ui.theme);
  const homeFeatureActivityListenerRef = useRef(null);

  const { activeFeatures, unusedFeatures, isLoading } = useHomeFeatures();
  const {
    resolveAction,
    isCreateWithIAOpen,
    onCloseCreateWithIA,
    isShareModalOpen,
    onCloseShareModal,
    competitorScopeModalsProps,
  } = useHomeFeatureActions();
  const { onOpenGallery, galleryModal } = useOutsideCampaignGallery();

  useEffect(() => {
    dispatch(
      startListenerHomeFeatureActivity({
        onReferenceAvailable: (ref) => {
          homeFeatureActivityListenerRef.current = ref;
        },
      })
    );

    return () => {
      stopListenerHomeFeatureActivity({
        listenerRef: homeFeatureActivityListenerRef.current,
      });
      homeFeatureActivityListenerRef.current = null;
    };
  }, [dispatch]);

  if (isLoading) {
    return (
      <ContainerPage hasMarginBottom={false} sx={{ height: "100%" }}>
        <Loader />
      </ContainerPage>
    );
  }

  return (
    <ContainerPage hasMarginBottom={false} sx={{ height: "100%" }}>
      <Helmet>
        <title>{`${theme.nameBusiness} - ${t(HOME_PAGE_TITLE)}`}</title>
      </Helmet>

      <Box
        sx={{
          display: "grid",
          gridTemplateAreas: `
            "title"
            "subtitle"
          `,
          width: "100%",
          px: 3,
          mt: 4,
          gap: 1,
        }}
      >
        <Box sx={{ gridArea: "title" }}>
          <TitlePage>{t(HOME_WELCOME_TITLE)}</TitlePage>
        </Box>

        <Box sx={{ gridArea: "subtitle", pb: { xs: 1, md: 0 } }}>
          <Typography variant="body1" sx={{ color: "#878383" }}>
            {t(HOME_SUBTITLE)}
          </Typography>
        </Box>

        <OnboardingSetupSteps />

        <Box
          sx={{
            mt: 2.5,
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          {HOME_FEATURES.map((feature) => {
            const Icon = feature.icon;
            const action = resolveAction(feature);
            const chipProps = getFeatureChipProps(action);

            return (
              <Chip
                key={feature.id}
                icon={<Icon size={16} aria-hidden="true" />}
                label={t(feature.titleKey)}
                variant="outlined"
                {...chipProps}
                sx={{
                  px: 0.5,
                  height: 36,
                  fontWeight: 500,
                  borderColor: INPUT_COLOR_BORDER,
                  bgcolor: "background.paper",
                  "& .MuiChip-icon": {
                    color: "text.secondary",
                    ml: 1,
                  },
                  ...(action.type !== "none" && {
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                  }),
                }}
              />
            );
          })}
        </Box>

        {activeFeatures.length > 0 ? (
          <Box
            component="section"
            aria-labelledby="continua-heading"
            sx={{ mt: 3 }}
          >
            <Box
              sx={{
                mb: 2,
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Typography id="continua-heading" variant="h6" fontWeight={600}>
                {t(HOME_SECTION_CONTINUE)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(HOME_SECTION_CONTINUE_HINT)}
              </Typography>
            </Box>

            <Grid container spacing={2}>
              {activeFeatures.map((feature) => (
                <Grid item xs={12} sm={6} lg={4} key={feature.id}>
                  <FeatureCard
                    feature={feature}
                    isDiscoverSection={false}
                    action={resolveAction(feature)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : null}

        {unusedFeatures.length > 0 ? (
          <Box
            component="section"
            aria-labelledby="descubre-heading"
            sx={{ mt: 5 }}
          >
            <Box
              sx={{
                mb: 2,
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Typography id="descubre-heading" variant="h6" fontWeight={600}>
                {t(HOME_SECTION_DISCOVER)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(HOME_SECTION_DISCOVER_HINT)}
              </Typography>
            </Box>

            <Grid container spacing={2}>
              {unusedFeatures.map((feature) => (
                <Grid item xs={12} sm={6} lg={4} key={feature.id}>
                  <FeatureCard
                    feature={feature}
                    isDiscoverSection={true}
                    action={resolveAction(feature)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : null}
      </Box>

      {isCreateWithIAOpen ? (
        <ModalCreateWithIA
          modalOpen={isCreateWithIAOpen}
          onCloseModal={onCloseCreateWithIA}
          onCallbackAfterSaveToGallery={onOpenGallery}
        />
      ) : null}

      <CompetitorScopeFlowModals
        {...competitorScopeModalsProps}
        onCallbackAfterSaveToGallery={onOpenGallery}
      />

      {galleryModal}

      {isShareModalOpen ? (
        <ShareModalMultiple
          item={null}
          modalOpen={isShareModalOpen}
          onCloseModal={onCloseShareModal}
        />
      ) : null}
    </ContainerPage>
  );
};

export default Dashboard;
